// ─────────────────────────────────────────────────────────────────────────────
// BEHAVIORAL FIT (BeFit) v2 — contextual scenarios, MOST / LEAST like me
//
// 24 everyday work situations. Each has four possible responses, one per work
// style (LTO, COM, STP, ATD). The candidate marks the response MOST like them and
// the one LEAST like them. There is no right or wrong answer: every response is
// a sensible, positive way of handling the situation, written to be equally
// attractive and about the same length, so no option is "the good one".
//
// Because the choice is forced, results describe where a person LEANS (their
// relative emphasis across the four styles), not how much of a quality they have.
// That is a profile, like DISC — not a score out of 100 and not a pass mark.
//
// These items are original to AXIS360 and have NOT yet been validated against
// outcomes or normed on a population. Treat results as a structured conversation
// starter, not as a predictor of performance.
// ─────────────────────────────────────────────────────────────────────────────

// The four work styles. Ids are the same as BeFit v1 so dashboards that read the
// four scale scores keep working.
export const BF2_STYLES = {
  LTO: { id:"LTO", label:"Leading & Team Orientation", short:"Leader", icon:"👥", color:"#7c3aed",
         tagline:"Takes the initiative, rallies people and shares ownership of outcomes." },
  COM: { id:"COM", label:"Communication", short:"Communicator", icon:"💬", color:"#1d4ed8",
         tagline:"Talks things through, listens, and keeps people informed and aligned." },
  STP: { id:"STP", label:"Pressure Handling", short:"Steady Hand", icon:"🧘", color:"#059669",
         tagline:"Stays composed and keeps momentum when things get demanding." },
  ATD: { id:"ATD", label:"Precision & Detail", short:"Precisionist", icon:"🔍", color:"#d97706",
         tagline:"Works carefully, checks the facts and follows things through exactly." },
};
export const BF2_DIM_IDS = ["LTO", "COM", "STP", "ATD"];

const S = (id, ctx, lto, com, stp, atd) => ({
  id, ctx,
  options: [
    { dim: "LTO", text: lto },
    { dim: "COM", text: com },
    { dim: "STP", text: stp },
    { dim: "ATD", text: atd },
  ],
});

// Option order here is a reference only: the survey shuffles the four responses for every candidate.
export const BF2_SCENARIOS = [
  S("bf2_01", "Your team's deadline has just been brought forward by two days.",
    "Take the lead: reassign tasks quickly and set a clear pace for everyone",
    "Get the team together to agree priorities and make sure everyone knows what changed",
    "Stay calm and focus on the highest-impact work, one step at a time",
    "Re-check the requirements and the plan so nothing important is missed in the rush"),
  S("bf2_02", "You receive instructions for a new task that are only partly clear.",
    "Decide on a sensible approach yourself and get others moving on it",
    "Ask the person who gave them a few direct questions until it is clear",
    "Start with the parts that are clear and stay relaxed about adjusting as you go",
    "Write down exactly what is and is not specified so you can work to it precisely"),
  S("bf2_03", "A teammate is falling behind on their workload.",
    "Step in and reorganise who does what so the team still delivers",
    "Have a supportive conversation to understand what is getting in their way",
    "Keep the team steady so the pressure does not spread to everyone else",
    "Look at exactly what is outstanding and help them tidy up the key items"),
  S("bf2_04", "A customer is upset about a mistake and is raising their voice.",
    "Take ownership of the situation and decide what will be done to put it right",
    "Listen fully, show that you understand, then explain clearly what happens next",
    "Stay composed and professional, and keep the conversation calm",
    "Check the facts of what happened so that you respond with accurate information"),
  S("bf2_05", "In a meeting, two colleagues strongly disagree about the way forward.",
    "Step in, weigh the options and guide the group towards a decision",
    "Help each person put their view across and look for the common ground",
    "Keep the tone steady and stop the discussion from becoming heated",
    "Ask for the evidence and the details behind each position before deciding"),
  S("bf2_06", "You discover an error in work that has already been shared.",
    "Take responsibility, tell those affected and lead the fix",
    "Let the people affected know straight away, openly and clearly",
    "Stay level-headed, do not dwell on it, and move quickly to correct it",
    "Trace exactly how it happened and put a check in place so it does not recur"),
  S("bf2_07", "A new person joins your team and needs to settle in.",
    "Give them real responsibility early and help them grow into it",
    "Take time to explain how things work and invite all of their questions",
    "Keep things relaxed so they feel comfortable while they find their feet",
    "Walk them through the processes step by step so they do things correctly"),
  S("bf2_08", "Three urgent requests arrive from different people at the same time.",
    "Decide what comes first and hand off whatever others can take on",
    "Tell each person when to expect their item so that expectations are clear",
    "Stay composed, take them one at a time and keep making progress",
    "List each request with its exact requirements so that none is overlooked"),
  S("bf2_09", "You are asked to present your work to senior leaders tomorrow.",
    "Build the story around the outcome and the decisions you want from them",
    "Concentrate on explaining the key points simply and clearly for that audience",
    "Prepare enough, then trust yourself to stay calm and adapt on the day",
    "Check every number and slide carefully so that the content is accurate"),
  S("bf2_10", "A major change to how your team works has just been announced.",
    "Help the team see a path through it and take the first steps yourself",
    "Talk it through with colleagues so everyone understands what it means for them",
    "Keep your composure and adapt to the new way of working without fuss",
    "Study the new procedures closely so you apply them correctly from day one"),
  S("bf2_11", "Your manager asks you for honest feedback on a colleague's work.",
    "Focus on how they could grow and what would help them own their work",
    "Be open and constructive, and explain your points so that they can act on them",
    "Keep it calm and balanced so that it does not become an emotional moment",
    "Point to specific examples and details so that the feedback is exact"),
  S("bf2_12", "You receive unexpected criticism of work you were proud of.",
    "Take it on board and decide how you will respond and improve",
    "Ask questions to understand the criticism and talk it through",
    "Stay composed, hear it out and keep it from affecting the rest of your day",
    "Go back through the work to find exactly where it could be better"),
  S("bf2_13", "You are asked to plan a project from scratch.",
    "Set the direction, assign owners and get the team behind the plan",
    "Consult the people involved so the plan reflects their input",
    "Build in some flexibility so the plan can absorb surprises calmly",
    "Map out each step and dependency carefully so that nothing is missed"),
  S("bf2_14", "You are given a long, repetitive task with a firm deadline.",
    "Look for a way to share the workload across the team",
    "Check in regularly so that others know how it is going",
    "Keep a steady rhythm and stay focused until it is done",
    "Follow the procedure carefully and check your output as you go"),
  S("bf2_15", "Your team is behind on a shared goal.",
    "Rally the team and set a clear plan to recover",
    "Talk openly about the reasons and agree the next steps together",
    "Keep morale and focus steady so that people continue to perform under pressure",
    "Review in detail where the gaps are and fix the specific causes"),
  S("bf2_16", "You are handing over an important piece of work to a colleague.",
    "Make sure they feel ready to own it and can run with it independently",
    "Explain the background and answer their questions until they are comfortable",
    "Stay flexible and relaxed and remain available as they get started",
    "Prepare a precise handover note that covers every step and detail"),
  S("bf2_17", "A decision is needed quickly and you do not have enough information to be certain.",
    "Make the call and take responsibility for the outcome",
    "Quickly consult the people who know most, then decide together",
    "Stay clear-headed and decide with what you have, ready to adjust",
    "Gather the key facts you can and check them before committing"),
  S("bf2_18", "Another team needs something that conflicts with your own priorities.",
    "Take the initiative to broker a decision with everyone involved",
    "Discuss it openly with them to understand their needs and reach agreement",
    "Stay constructive and keep it from turning into a source of tension",
    "Look at the actual commitments and details on both sides before responding"),
  S("bf2_19", "You need to learn a new software tool in a short time.",
    "Learn it quickly, then show others how to use it",
    "Ask colleagues and the trainer questions as you go along",
    "Stay patient and keep experimenting without getting frustrated",
    "Follow the guide step by step and make sure you understand each function"),
  S("bf2_20", "Your team has just completed a successful project.",
    "Make sure everyone's contribution is recognised and look to what comes next",
    "Share the result and what you learned with the wider organisation",
    "Take it in your stride, stay grounded and carry on steadily",
    "Review what went well and what to tighten up for next time"),
  S("bf2_21", "It is unclear who owns an important task.",
    "Take it on yourself, or assign it to someone, so that it does not slip",
    "Raise it with the group and agree together on who owns it",
    "Stay unbothered by the gap and work out a sensible way forward",
    "Check the records and the process to see who is meant to own it"),
  S("bf2_22", "Your focused work keeps being interrupted by requests from others.",
    "Set boundaries and organise how the team handles requests",
    "Tell people politely when you will be available and agree timings",
    "Stay patient and switch between tasks without losing your composure",
    "Block out time for careful work and keep a list so that nothing is forgotten"),
  S("bf2_23", "You have to tell a colleague or client some disappointing news.",
    "Own the message and come with a plan for what happens next",
    "Be honest and clear, and give them space to respond",
    "Stay steady while you deliver it, whatever the reaction",
    "Make sure the facts and details are right before you share it"),
  S("bf2_24", "You have to work closely with someone you find difficult.",
    "Take the lead in setting out how the two of you will work together",
    "Talk to them directly about how to make the working relationship smoother",
    "Stay professional and even-tempered whatever happens",
    "Agree clearly who does what, and put it in writing where it helps"),
];
